<script setup>
import { ref, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

const props = defineProps({
    users: Array,
});

// State
const histories = ref([]);
const loading = ref(true);
const showStats = ref(false);
const stats = ref({
    total_logins: 0,
    unique_ips: 0,
    desktop: 0,
    mobile: 0
});

// Filter state
const filters = ref({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    user_id: '',
    device: ''
});

// Load data
const loadHistory = async () => {
    loading.value = true;
    try {
        const params = new URLSearchParams(filters.value).toString();
        const response = await axios.get('/admin/login-history/data?' + params);
        if (response.data.status === 'success') {
            histories.value = response.data.data;
        }
    } catch (error) {
        console.error('Error loading history:', error);
    } finally {
        loading.value = false;
    }
};

const loadStats = async () => {
    try {
        const params = new URLSearchParams(filters.value).toString();
        const response = await axios.get('/admin/login-history/stats?' + params);
        if (response.data.status === 'success') {
            const data = response.data.data;
            stats.value.total_logins = data.total_logins;
            stats.value.unique_ips = data.unique_ips;
            
            // Count by device
            stats.value.desktop = 0;
            stats.value.mobile = 0;
            data.logins_by_device?.forEach(item => {
                if (item.device === 'Desktop') stats.value.desktop = item.count;
                else if (item.device === 'Mobile') stats.value.mobile = item.count;
            });
            
            showStats.value = true;
        }
    } catch (error) {
        console.error('Error loading stats:', error);
    }
};

const applyFilter = () => {
    loadHistory();
    loadStats();
};

const resetFilter = () => {
    filters.value = {
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        user_id: '',
        device: ''
    };
    loadHistory();
    loadStats();
};

const clearOldLogs = async () => {
    if (!confirm('Hapus semua log login yang lebih dari 90 hari?')) return;
    
    try {
        const response = await axios.post('/admin/login-history/clear-old', { days: 90 });
        if (response.data.status === 'success') {
            alert(response.data.message);
            loadHistory();
            loadStats();
        }
    } catch (error) {
        alert('Terjadi kesalahan saat menghapus log');
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('id-ID');
};

onMounted(() => {
    loadHistory();
    loadStats();
});
</script>

<template>
    <Head title="Login History Report" />

    <div class="container-full">
        <!-- Content Header -->
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div>
                    <h4 class="page-title">Login History Report</h4>
                    <nav>
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                            <li class="breadcrumb-item active">Login History</li>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <!-- Main content -->
        <section class="content">
            <!-- Filter Box -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title"><i class="fa fa-filter me-10"></i>Filter Laporan</h4>
                        </div>
                        <div class="box-body">
                            <form @submit.prevent="applyFilter" class="row">
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Tanggal Mulai</label>
                                        <input type="date" class="form-control" v-model="filters.start_date">
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Tanggal Akhir</label>
                                        <input type="date" class="form-control" v-model="filters.end_date">
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>User</label>
                                        <select class="form-control" v-model="filters.user_id">
                                            <option value="">Semua User</option>
                                            <option v-for="user in users" :key="user.id" :value="user.id">
                                                {{ user.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label>Device</label>
                                        <select class="form-control" v-model="filters.device">
                                            <option value="">Semua Device</option>
                                            <option value="Desktop">Desktop</option>
                                            <option value="Mobile">Mobile</option>
                                            <option value="Tablet">Tablet</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-12">
                                    <button type="submit" class="btn btn-primary">
                                        <i class="fa fa-search me-5"></i> Tampilkan
                                    </button>
                                    <button type="button" class="btn btn-secondary" @click="resetFilter">
                                        <i class="fa fa-refresh me-5"></i> Reset
                                    </button>
                                    <button type="button" class="btn btn-warning" @click="clearOldLogs">
                                        <i class="fa fa-trash me-5"></i> Hapus Log Lama (>90 hari)
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div v-if="showStats" class="row">
                <div class="col-lg-3 col-6">
                    <div class="box">
                        <div class="box-body text-center">
                            <h2 class="mb-0 fw-700 text-primary">{{ stats.total_logins.toLocaleString() }}</h2>
                            <p class="text-muted mb-0">Total Login</p>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 col-6">
                    <div class="box">
                        <div class="box-body text-center">
                            <h2 class="mb-0 fw-700 text-success">{{ stats.unique_ips.toLocaleString() }}</h2>
                            <p class="text-muted mb-0">Unique IPs</p>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 col-6">
                    <div class="box">
                        <div class="box-body text-center">
                            <h2 class="mb-0 fw-700 text-info">{{ stats.desktop.toLocaleString() }}</h2>
                            <p class="text-muted mb-0">Desktop</p>
                        </div>
                    </div>
                </div>
                <div class="col-lg-3 col-6">
                    <div class="box">
                        <div class="box-body text-center">
                            <h2 class="mb-0 fw-700 text-warning">{{ stats.mobile.toLocaleString() }}</h2>
                            <p class="text-muted mb-0">Mobile</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div class="row">
                <div class="col-12">
                    <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title">Riwayat Login</h4>
                        </div>
                        <div class="box-body">
                            <div class="table-responsive">
                                <table class="table table-bordered table-striped">
                                    <thead>
                                        <tr>
                                            <th width="5%">No</th>
                                            <th width="15%">User</th>
                                            <th width="15%">Email</th>
                                            <th width="12%">IP Address</th>
                                            <th width="18%">Waktu Login</th>
                                            <th width="12%">Browser</th>
                                            <th width="10%">Device</th>
                                            <th width="13%">Platform</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="8" class="text-center p-5">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="histories.length === 0">
                                            <td colspan="8" class="text-center text-muted p-5">
                                                Tidak ada data login history.
                                            </td>
                                        </tr>
                                        <tr v-else v-for="(history, index) in histories" :key="history.id">
                                            <td>{{ index + 1 }}</td>
                                            <td>{{ history.user?.name || 'N/A' }}</td>
                                            <td>{{ history.user?.email || 'N/A' }}</td>
                                            <td><code>{{ history.ip_address }}</code></td>
                                            <td>
                                                <span class="d-block">{{ formatDate(history.login_at) }}</span>
                                                <small class="text-muted">{{ formatTime(history.login_at) }}</small>
                                            </td>
                                            <td>{{ history.browser || '-' }}</td>
                                            <td>
                                                <i :class="['fa', 'me-5', history.device === 'Mobile' ? 'fa-mobile' : (history.device === 'Tablet' ? 'fa-tablet' : 'fa-desktop')]"></i>
                                                {{ history.device || '-' }}
                                            </td>
                                            <td>{{ history.platform || '-' }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
